import { useMemo } from "react";
import { useQuery, useMutation, useMutationState, useQueryClient } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { BuildingProject, CreateProjectData, UpdateProjectData } from "@/types/projects";
import { bindTrackToCurrentUser, track } from "@/lib/posthog";
import { useAuth } from "@/hooks/useAuth";
import { useOrganizationContext } from "@/lib/context/OrganizationContext";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { useStores } from "@/stores";
import { billingGuardMessage } from "@/domains/billing/messages";
import { toProjectStatus, type ProjectStatus } from "@/domains/projects/status/projectStatus";
import {
  ensureProjectStatusAvailable,
  isProjectStatusEnabled,
} from "@/domains/projects/status/projectStatusAvailability";
import { safeErrorCode } from "@/domains/projects/status/safeErrorCode";
import { logger } from "@/utils/logger";

/**
 * @param options.list Whether to fetch the project LIST. A caller that only
 *   wants the mutations passes `false` and pays for none of it — the create
 *   dialog is mounted for the whole building page (see
 *   `CreateProjectDialogProvider`), so leaving its query on charged every
 *   building navigation a `building_projects` + `user_profiles` round trip,
 *   including for users who can never open it. Reads of `projects` /
 *   `archivedProjects` then see whatever a real consumer of the same
 *   `["building-projects", buildingId]` entry has cached, and nothing more.
 */
export function useProjects(buildingId: string, { list = true }: { list?: boolean } = {}) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { userProfile } = useAuth();
  const { currentOrganization } = useOrganizationContext();
  const { t } = useLanguage();
  const { projectFolderStore } = useStores();

  // The project list lives in TWO caches: this hook's React Query key and the
  // MST ProjectFolderStore that backs the sidebar project picker and the
  // chatbot folder picker. useBuildingScope loads the MST options only once
  // per (org, building), so after a create/update we must RELOAD them in place
  // (not just invalidate — nothing would re-trigger the load, leaving the
  // sidebar picker empty or stale until a full page refresh).
  const refreshProjectFolderStore = () => {
    const orgId = currentOrganization?.id;
    const buildingIdNum = parseInt(buildingId, 10);
    if (!orgId || !Number.isFinite(buildingIdNum)) return;
    void projectFolderStore.loadProjectOptions(orgId, buildingIdNum).catch(() => {
      // Non-fatal: the header keeps its previous list; the error is surfaced
      // via projectFolderStore.projectsError.
    });
  };

  const {
    data: allProjects = [],
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: ["building-projects", buildingId],
    queryFn: async () => {
      // `*` can include project_status even when the probe could not confirm
      // availability, so keep those rows on the dormant active fallback.
      await ensureProjectStatusAvailable();
      const { data, error } = await supabase
        .from("building_projects")
        .select("*")
        .eq("building_id", parseInt(buildingId, 10))
        .eq("hidden", false)
        .order("created_at", { ascending: false });

      if (error) throw error;

      // building_projects has no FK to user_profiles, so resolve names with a
      // single batched lookup over the unique creator/modifier IDs.
      const userIds = Array.from(
        new Set(
          data
            .flatMap((p) => [p.created_by, p.modified_by])
            .filter((id): id is number => id != null)
        )
      );

      const userNames = new Map<number, string>();
      if (userIds.length > 0) {
        const { data: users, error: usersError } = await supabase
          .from("user_profiles")
          .select("id, first_name, last_name")
          .in("id", userIds);
        if (usersError) throw usersError;
        for (const u of users ?? []) {
          const name = [u.first_name, u.last_name].filter(Boolean).join(" ").trim();
          if (name) userNames.set(u.id, name);
        }
      }

      return data.map((project) => ({
        ...project,
        id: String(project.id),
        building_id: String(project.building_id),
        created_by: String(project.created_by),
        created_by_name: userNames.get(project.created_by),
        modified_by: project.modified_by ? String(project.modified_by) : undefined,
        modified_by_name:
          project.modified_by != null ? userNames.get(project.modified_by) : undefined,
        archived: project.archived ?? false,
        status: toProjectStatus(isProjectStatusEnabled() ? project.project_status : null),
      })) as BuildingProject[];
    },
    enabled: !!buildingId && list,
  });

  // The cache entry keeps the full (both-states) list as its canonical shape;
  // derive the active/archived partitions per-render (see CLAUDE.md — never
  // reshape a shared queryKey's data inside one consumer). `projects` excludes
  // archived so every selector/picker that reads it hides archived projects for
  // free; DashboardProjectsGrid uses `archivedProjects` for its "show archived"
  // view.
  const projects = useMemo(() => allProjects.filter((p) => !p.archived), [allProjects]);
  const archivedProjects = useMemo(() => allProjects.filter((p) => p.archived), [allProjects]);

  const createProjectMutation = useMutation({
    mutationFn: async (projectData: CreateProjectData) => {
      // Validate required context
      if (!userProfile?.id) {
        throw new Error("User authentication required to create projects");
      }

      // org_id must be the BUILDING's org, not the ambient org-switcher
      // context: a multi-org user can deep-link into a building while a
      // different org is selected in the header, and an ambient org_id would
      // create a project invisible to the building's own org (e.g. missing
      // from get_org_projects_overview counts).
      const buildingIdNum = parseInt(projectData.building_id, 10);
      const { data: building, error: buildingError } = await supabase
        .from("buildings")
        .select("org_id")
        .eq("id", buildingIdNum)
        .single();

      if (buildingError) throw buildingError;

      const { data, error } = await supabase
        .from("building_projects")
        .insert({
          building_id: buildingIdNum,
          name: projectData.name,
          description: projectData.description,
          created_by: parseInt(String(userProfile.id), 10),
          org_id: building.org_id,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (data, variables) => {
      track("project_created", { source: variables.source ?? "modal" });
      queryClient.invalidateQueries({ queryKey: ["building-projects", buildingId] });
      // Insert the new project into the MST options BEFORE any mutate-level
      // onSuccess scopes to it (this hook-level callback runs first): the
      // reload below is async and its failure is swallowed, and an id missing
      // from the options gets stripped by useBuildingScope's reconcile — the
      // pickers' create-then-scope flow would silently land back on the
      // "select a project" gate.
      // Key both the insert and the reload off the AMBIENT org — that is the
      // key every consumer reads (useBuildingScope and the chatbot picker call
      // getProjectOptions with currentOrganization.id), so a write under any
      // other key (e.g. the row's org_id) never reaches the pickers and leaves
      // the ambient entry stale. In the rare deep-link case where the ambient
      // org isn't the building's org (see mutationFn), the org-filtered read
      // model can't show the new project at all — a consumer-side keying
      // limitation this write can't fix.
      const buildingIdNum = parseInt(buildingId, 10);
      if (currentOrganization?.id && Number.isFinite(buildingIdNum)) {
        projectFolderStore.addProjectOption(currentOrganization.id, buildingIdNum, {
          id: data.id,
          name: data.name,
        });
      }
      refreshProjectFolderStore();
      toast({
        title: t.common.success,
        description: t.projects.create.success,
      });
    },
    onError: (error) => {
      console.error("Create project error:", error);
      // A billing write guard refused the INSERT into building_projects. It is
      // actionable — settle the invoice, or restore the removed building — so it
      // is named rather than folded into the generic failure.
      const billingRefusal = billingGuardMessage(error, t);
      let errorMessage = t.projects.create.createFailed;

      if (billingRefusal) {
        errorMessage = billingRefusal;
      } else if (error.message.includes("authentication required")) {
        errorMessage = t.projects.create.authRequired;
      } else if (error.message.includes("Organization context required")) {
        errorMessage = t.projects.create.orgRequired;
      }

      toast({
        title: t.common.error,
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  const updateProjectMutation = useMutation({
    mutationFn: async (projectData: UpdateProjectData) => {
      // Validate required context
      if (!userProfile?.id) {
        throw new Error("User authentication required to update projects");
      }

      if (!currentOrganization?.id) {
        throw new Error("Organization context required to update projects");
      }

      const { data, error } = await supabase
        .from("building_projects")
        .update({
          name: projectData.name,
          // Coerce undefined → null so clearing the description actually
          // persists. supabase-js JSON.stringify drops undefined keys from the
          // PATCH body, which would silently omit the column and keep the old
          // value; null tells PostgREST to set it to NULL.
          description: projectData.description ?? null,
          modified_by: parseInt(String(userProfile.id), 10),
          modified_at: new Date().toISOString(),
        })
        .eq("id", parseInt(projectData.id, 10))
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["building-projects", buildingId] });
      refreshProjectFolderStore();
      toast({
        title: t.common.success,
        description: t.projects.edit.success,
      });
    },
    onError: (error) => {
      console.error("Update project error:", error);
      let errorMessage = t.projects.edit.failed;

      if (error.message.includes("authentication required")) {
        errorMessage = t.projects.edit.authRequired;
      } else if (error.message.includes("Organization context required")) {
        errorMessage = t.projects.edit.orgRequired;
      }

      toast({
        title: t.common.error,
        description: errorMessage,
        variant: "destructive",
      });
    },
  });

  // Archive / restore a project — a plain UPDATE of the archived flag, already
  // gated by the projects_update RLS policy (MODIFY_DATA), exactly like the
  // buildings.archived toggle (buildings/repository.ts setArchived).
  const archiveProjectMutation = useMutation({
    mutationFn: async ({ id, archived }: { id: string; archived: boolean }) => {
      if (!userProfile?.id) {
        throw new Error("User authentication required to archive projects");
      }

      // The is_default project is the building's catch-all channel — never let it
      // be archived, or it vanishes from every picker while still being selected
      // as the default (getDefaultProjectId only filters hidden). The button hides
      // this option, but guard here so any other caller is covered too.
      if (archived) {
        // Cache first, DB second: a `list: false` caller has no cached list, and
        // even a listing one omits hidden projects — so an absent row means "not
        // known", never "not the default". Reading the flag keeps the guard true
        // for every caller instead of passing by default for the ones that
        // opted out of the query.
        let isDefault = allProjects.find((p) => p.id === id)?.is_default;
        if (isDefault === undefined) {
          // `maybeSingle`, not `single`: a row that SELECT cannot see is not a
          // reason to fail an archive this guard was never meant to police —
          // `single` answers no rows with PGRST116, which surfaces as a generic
          // "archive failed" for what may be a perfectly permitted UPDATE. With
          // nothing read the guard stands down and the UPDATE's own RLS decides;
          // it returns its representation through the same SELECT, so an
          // invisible row still cannot be archived silently.
          const { data: row, error: readError } = await supabase
            .from("building_projects")
            .select("is_default")
            .eq("id", parseInt(id, 10))
            .maybeSingle();
          if (readError) throw readError;
          isDefault = row?.is_default ?? false;
        }
        if (isDefault) throw new Error("DEFAULT_PROJECT_PROTECTED");
      }

      const now = new Date().toISOString();
      const actorId = parseInt(String(userProfile.id), 10);
      const { data, error } = await supabase
        .from("building_projects")
        .update({
          archived,
          archived_at: archived ? now : null,
          archived_by: archived ? actorId : null,
          modified_by: actorId,
          modified_at: now,
        })
        .eq("id", parseInt(id, 10))
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["building-projects", buildingId] });
      refreshProjectFolderStore();
      toast({
        title: t.common.success,
        description: variables.archived
          ? t.projects.archive.archivedSuccess
          : t.projects.archive.restoredSuccess,
      });
    },
    onError: (error, variables) => {
      void logger.error(
        "database",
        "project_archive_update",
        "Project archive state update failed",
        new Error(`Project archive update failed (${safeErrorCode(error)})`),
        { projectId: variables.id, archived: variables.archived }
      );
      const description =
        billingGuardMessage(error, t) ??
        (error.message === "DEFAULT_PROJECT_PROTECTED"
          ? t.projects.archive.defaultProtected
          : variables.archived
            ? t.projects.archive.archiveFailed
            : t.projects.archive.restoreFailed);
      toast({
        title: t.common.error,
        description,
        variant: "destructive",
      });
    },
  });

  // Mark a project active / inactive. Only project_status is written: the
  // building_projects_project_status_guard trigger checks MANAGE_PROJECT_STATUS
  // (owner/admin) and stamps project_status_changed_{at,by} itself, so the
  // attribution can't be supplied — or forged — from here. A refusal is 42501.
  const statusMutationKey = ["project-status", buildingId];
  const pendingStatusChanges = useMutationState({
    filters: { mutationKey: statusMutationKey, status: "pending" },
    select: (mutation) =>
      mutation.state.variables as { id: string; status: ProjectStatus } | undefined,
  });
  const pendingStatusProjectIds = new Set(
    pendingStatusChanges.flatMap((change) => (change ? [change.id] : []))
  );
  const setProjectStatusMutation = useMutation({
    mutationKey: statusMutationKey,
    mutationFn: async ({ id, status }: { id: string; status: ProjectStatus }) => {
      // The control only renders once the column is known to exist; this is
      // the backstop for any caller that reaches the mutation regardless.
      if (!(await ensureProjectStatusAvailable())) throw new Error("PROJECT_STATUS_DISABLED");
      const { data, error } = await supabase
        .from("building_projects")
        .update({ project_status: status })
        .eq("id", parseInt(id, 10))
        .select("id, project_status")
        .maybeSingle();
      if (error) throw error;
      if (!data) {
        throw Object.assign(new Error("Project status update was not permitted"), {
          code: "42501",
        });
      }
      return data;
    },
    onMutate: () => ({
      track: bindTrackToCurrentUser(currentOrganization?.id),
      organizationId: currentOrganization?.id,
      buildingId,
    }),
    onSuccess: async (_data, variables, context) => {
      context?.track("project_status_changed", { status: variables.status });
      refreshProjectFolderStore();
      // Awaited: the card's blip and the pickers' tabs read these caches, and a
      // toast announcing a change the UI doesn't show yet reads as a failure.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["building-projects", buildingId] }),
        queryClient.invalidateQueries({ queryKey: ["org-projects-overview"] }),
      ]);
      toast({
        title: t.common.success,
        description:
          variables.status === "inactive"
            ? t.projects.status.markedInactive
            : t.projects.status.markedActive,
      });
    },
    onError: (error, variables, context) => {
      const errorCode = safeErrorCode(error);
      const reportableError = new Error(`Project status update failed (${errorCode})`);
      const sentryEventId = Sentry.captureException(reportableError, {
        tags: {
          area: "projects",
          operation: "project_status_update",
          error_code: errorCode,
        },
        extra: {
          projectId: variables.id,
          targetStatus: variables.status,
          buildingId: context?.buildingId ?? buildingId,
          organizationId: context?.organizationId,
        },
        fingerprint: ["projects", "project-status", "update"],
      });
      void logger.error(
        "database",
        "project_status_update",
        "Failed to update project status",
        reportableError,
        {
          project_id: variables.id,
          target_status: variables.status,
          building_id: context?.buildingId ?? buildingId,
          organization_id: context?.organizationId,
          error_code: errorCode,
          sentry_event_id: sentryEventId,
        }
      );
      toast({
        title: t.common.error,
        description:
          errorCode === "42501" ? t.projects.status.noPermission : t.projects.status.changeFailed,
        variant: "destructive",
      });
    },
  });

  return {
    projects,
    archivedProjects,
    isLoading,
    isError,
    refetch,
    createProject: createProjectMutation.mutate,
    isCreating: createProjectMutation.isPending,
    updateProject: updateProjectMutation.mutate,
    isUpdating: updateProjectMutation.isPending,
    archiveProject: archiveProjectMutation.mutate,
    isArchiving: archiveProjectMutation.isPending,
    setProjectStatus: setProjectStatusMutation.mutate,
    isSettingProjectStatus: setProjectStatusMutation.isPending,
    pendingStatusProjectIds,
  };
}
